/*
 * Drag-to-slide, measured against the old theme's PhotoSwipe viewer: the strip follows the
 * pointer 1:1; a release past DRAG_THRESHOLD glides to the neighbour (~400ms, cubic ease-out),
 * a shorter one eases back (~330ms); past the first or last image it pulls at EDGE_FRICTION
 * and springs back. Arrows, thumbnails and arrow keys glide the same way. Opening jumps
 * straight to the clicked image.
 */
const DRAG_THRESHOLD = 30;
const EDGE_FRICTION = 0.35;
const SLIDE_MS = 400;
const SNAP_BACK_MS = 330;

export class ProductMediaModal extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slides = this.querySelectorAll('.product-media-modal__slide');
    this.thumbs = this.querySelectorAll('.product-media-modal__thumb');
    this.track = this.querySelector('.product-media-modal__main-image');
    this.totalSlides = this.slides.length;
    this.drag = null;

    const triggers = document.querySelectorAll('media-gallery .light-box-zoom-trigger');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const mediaId = trigger.getAttribute('data-media-id');
        this.openAtMedia(mediaId);
      });
    });

    const closeBtn = this.querySelector('.product-media-modal__close');
    const backBtn = this.querySelector('.product-media-modal__back');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (backBtn) backBtn.addEventListener('click', () => this.close());

    const prevBtn = this.querySelector('.product-media-modal__arrow--prev');
    const nextBtn = this.querySelector('.product-media-modal__arrow--next');
    if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.next());

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.getAttribute('data-thumb-index'), 10);
        this.goTo(idx, SLIDE_MS);
      });
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    if (this.track) {
      this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.track.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.track.addEventListener('pointerup', (e) => this.onPointerUp(e));
      this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e));
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }

  openAtMedia(mediaId) {
    let idx = 0;
    this.slides.forEach((slide, i) => {
      if (slide.getAttribute('data-media-id') === mediaId) idx = i;
    });
    this.goTo(idx);
    this.open();
  }

  open() {
    document.body.style.overflow = 'hidden';
    this.setAttribute('open', '');
    this.focus();
  }

  close() {
    document.body.style.overflow = '';
    this.removeAttribute('open');
  }

  prev() {
    const idx = this.currentIndex > 0 ? this.currentIndex - 1 : this.totalSlides - 1;
    this.goTo(idx, SLIDE_MS);
  }

  next() {
    const idx = this.currentIndex < this.totalSlides - 1 ? this.currentIndex + 1 : 0;
    this.goTo(idx, SLIDE_MS);
  }

  /**
   * Show an image. Instant unless `settleMs` is given, which glides the strip there (drag release).
   * @param {number} idx
   * @param {number} [settleMs]
   */
  goTo(idx, settleMs = 0) {
    this.slides[this.currentIndex]?.classList.remove('active');
    this.thumbs[this.currentIndex]?.classList.remove('active');
    this.currentIndex = idx;
    this.slides[this.currentIndex]?.classList.add('active');
    this.thumbs[this.currentIndex]?.classList.add('active');

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
      // Load and decode the neighbours ahead of time, so a glide never stalls on decoding a
      // large image or slides in empty, then pops.
      if (Math.abs(i - idx) <= 1) {
        const img = slide.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
        img?.decode?.().catch(() => {});
      }
    });

    if (!this.track) return;
    this.track.style.setProperty('--pmm-settle', `${settleMs}ms`);
    this.track.style.setProperty('--pmm-drag', '0px');
    this.track.style.setProperty('--pmm-index', idx);
  }

  onPointerDown(e) {
    if (this.totalSlides < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
    this.drag = { startX: e.clientX, dx: 0, id: e.pointerId };
    this.track.style.setProperty('--pmm-settle', '0ms');
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(e.pointerId);
  }

  onPointerMove(e) {
    if (!this.drag || e.pointerId !== this.drag.id) return;
    let dx = e.clientX - this.drag.startX;
    const pastStart = dx > 0 && this.currentIndex === 0;
    const pastEnd = dx < 0 && this.currentIndex === this.totalSlides - 1;
    if (pastStart || pastEnd) dx *= EDGE_FRICTION;
    this.drag.dx = dx;
    this.track.style.setProperty('--pmm-drag', `${dx}px`);
  }

  onPointerUp(e) {
    if (!this.drag || e.pointerId !== this.drag.id) return;
    const { dx } = this.drag;
    this.drag = null;
    this.track.classList.remove('is-dragging');

    let idx = this.currentIndex;
    if (dx <= -DRAG_THRESHOLD && this.currentIndex < this.totalSlides - 1) idx += 1;
    if (dx >= DRAG_THRESHOLD && this.currentIndex > 0) idx -= 1;
    this.goTo(idx, idx === this.currentIndex ? SNAP_BACK_MS : SLIDE_MS);
  }
}

if (!customElements.get('product-media-modal')) {
  customElements.define('product-media-modal', ProductMediaModal);
}
